import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Chart as ChartJS, CategoryScale, LinearScale, BarController, BarElement, Tooltip, Legend } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { Header, Button, Message, Groupper, Label, Table, Modal, Input, Toast, Toolbar } from "react-frontier";
import { Statistic } from 'semantic-ui-react';
import { useBreadcrumbs, useUser } from "../hooks";
import { EmploymentScheduleType, UserAssistance, UserClass, SetLoading } from "../SiteClasses";
import { SearchUserModal } from "../components";
import { bindClose, bindClick, addCommas, getCheckInStatus, getStatusColor, getStatusLabel } from "../SiteUtil";
import UserAccess from "../UserAccess";
import Validator from "../Validator";
import API from "../API";
import moment from "moment";

ChartJS.register(CategoryScale, LinearScale, BarController, BarElement, Tooltip, Legend);

const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

enum Modals{
	INFO = 1,
	EDIT = 2,
	USERS = 3
}

const CHART_OPTIONS = {
	indexAxis: 'x' as const,
	responsive: true,
	maintainAspectRatio: false,
	onClick: null as any,
	plugins: {
		legend: { display: false },
		tooltip: {
			callbacks: {
				label: (context: any) => {
					if(!context.raw) return '';
					var [start, end] = [Math.floor(context.raw[0]), Math.floor(context.raw[1])];
					var formatTime = (mins: number) => `${String(Math.floor(mins/60)).padStart(2,'0')}:${String(mins%60).padStart(2,'0')}`;
					var duration = end - start;
					return [
						`${formatTime(start)} - ${formatTime(end)}`,
						`Duración: ${Math.floor(duration/60)}h ${duration%60}m`
					];
				},
				title: (context: any) => context?.[0]?.dataset?.label || 'Asistencia'
			}
		}
	},
	scales: {
		x: { grid: { display: true, offset: true }, stacked: true, offset: true, position: 'bottom' as const },
		y: {
			type: 'linear' as const,
			min: 0,
			max: 1440,
			stacked: false,
			position: 'left' as const,
			ticks: {
				stepSize: 60,
				callback: (value: any) => `${String(Math.floor(value/60)).padStart(2,'0')}:00`,
				font: { size: 11 }
			},
			grid: { display: true, color: '#e0e0e0' }
		}
	},
	barPercentage: 0.7,
	categoryPercentage: 0.85
};

const CHART_PLUGINS = [{
	id: 'barLabels',
	afterDatasetsDraw: (chart: any) => {
		var ctx = chart.ctx;
		chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
			var meta = chart.getDatasetMeta(datasetIndex);
			meta.data.forEach((bar: any, index: number) => {
				var data = dataset.data[index];
				if(!data || !Array.isArray(data)) return;
				
				var duration = Math.floor(data[1]) - Math.floor(data[0]);
				if(duration < 30) return;
				
				var hours = Math.floor(duration / 60);
				var label = hours > 0 ? `${hours}h ${duration % 60}m` : `${duration % 60}m`;
				
				ctx.save();
				ctx.fillStyle = '#fff';
				ctx.font = 'bold 11px sans-serif';
				ctx.textAlign = 'center';
				ctx.textBaseline = 'middle';
				ctx.fillText(label, bar.x, (bar.y + bar.base) / 2);
				ctx.restore();
			});
		});
	}
}];

var EmployeeAssistance = ()=>{
	var { id } = useParams<{ id?: string }>();
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [userAssistance, setUserAssistance] = useState<UserAssistance[]>(null);
	var [currentWeekStart, setCurrentWeekStart] = useState(moment().startOf('isoWeek'));
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [selectedLog, setSelectedLog] = useState<UserAssistance>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>([]);
	var [selectedUser, setSelectedUser] = useState<UserClass>(null);
	var [loadingUser, setLoadingUser] = useState(false);
	var [downloadingReport, setDownloadingReport] = useState(false);

	useEffect(()=> setTitle('Asistencia de Empleado'), []);

	useEffect(()=>{
		if(!id || selectedUser) return;
		setLoadingUser(true);
		API.getUser(parseInt(id)).then(res=>{
			if(res.error) return setLoadError('No se pudo cargar el usuario desde la URL');
			setSelectedUser(res.data.user);
		}).catch(()=> setLoadError('Hubo un error cargando el usuario'))
		.finally(()=> setLoadingUser(false));
	}, [id]);

	useEffect(()=>{
		if(!selectedUser) return;
		setUserAssistance(null);
		setLoadError(null);
		var weekStart = currentWeekStart.clone().startOf('day').unix();
		var weekEnd = currentWeekStart.clone().add(6, 'days').endOf('day').unix();
		API.getUserAssistance(selectedUser.user_id, weekStart, weekEnd).then(res=>{
			if(res.error) return setLoadError(res.message);
			setUserAssistance(res.data);
		}).catch(()=> setLoadError('Hubo un error cargando la asistencia'));
	}, [selectedUser, currentWeekStart]);

	var chartData = useMemo(()=>{
		if(!userAssistance?.length) return null;
		var startOfWeek = currentWeekStart.clone().startOf('day');
		var labels = Array.from({length: 7}, (_, i) => DAYS[startOfWeek.clone().add(i, 'days').day()]);

		var datasets = userAssistance.map(a => {
			if(!a.date_start) return null;
			var logMoment = moment.unix(a.date_start);
			var dayIndex = logMoment.diff(startOfWeek, 'days');
			if(dayIndex < 0 || dayIndex > 6) return null;

			var start = logMoment;
			var end = a.date_end && a.date_end > 0 ? moment.unix(a.date_end) : moment();
			var dataArray = Array(7).fill(null);
			dataArray[dayIndex] = [start.hour() * 60 + start.minute(), end.hour() * 60 + end.minute()];
			
			var color = getStatusColor(getCheckInStatus(a));
			
			return {
				label: a.employment_name || 'Trabajo',
				data: dataArray,
				backgroundColor: color,
				borderColor: color,
				borderWidth: 1,
				borderRadius: 4,
				barThickness: 60,
				log: a
			};
		}).filter(Boolean);

		return { labels, datasets };
	}, [userAssistance, currentWeekStart]);

	var totalHours = useMemo(()=> 
		(userAssistance?.reduce((acc, log) => 
			acc + (!log.log_type_id && log.type_id !== EmploymentScheduleType.REST && log.date_start && log.date_end && log.date_end > 0 ? log.date_end - log.date_start : 0), 0
		) || 0) / 3600
	, [userAssistance]);

	var totalPlanned = useMemo(()=> 
		(userAssistance?.reduce((acc, log) => 
			acc + (log.time_start && log.time_end ? log.time_end - log.time_start : 0), 0
		) || 0) / 60
	, [userAssistance]);

	var formatWeekRange = () => 
		`${currentWeekStart.format('DD/MM/YYYY')} - ${currentWeekStart.clone().add(6, 'days').format('DD/MM/YYYY')}`;

	var downloadReport = ()=>{
		if(!selectedUser || downloadingReport) return;

		var dateStart = currentWeekStart.clone().startOf('day').unix();
		var dateEnd = currentWeekStart.clone().add(6, 'days').endOf('day').unix();

		setDownloadingReport(true);
		API.downloadEmploymentAssistanceReport(dateStart, dateEnd, selectedUser.user_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Reporte descargado correctamente');
		}).catch(()=>{
			Toast.error('Hubo un error descargando el reporte.');
		}).finally(()=>{
			setDownloadingReport(false);
		});
	};

	var onUserSelect = (user: UserClass) => {
		setSelectedUser(user);
		setShownModal(null);
	};

	var onBarClick = (event: any, elements: any[], chart: any) => {
		if(!elements.length) return;
		var log = chart.data.datasets[elements[0].datasetIndex].log;
		setSelectedLog({...log});
		setModalPrompts([]);
		setShownModal(Modals.INFO);
	};

	var edit = (setLoading: SetLoading) => {
		var { valid, prompts } = Validator(selectedLog, {
			log_id: [{ rule: 'empty', prompt: 'Horario' }],
			date_start: [{ rule: 'empty', prompt: 'Hora de entrada' }],
			date_end: [{ rule: 'empty', prompt: 'Hora de salida' }],
		});
		setModalPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.employmentEditCheckIn(selectedLog.log_id, selectedLog.date_start, selectedLog.date_end).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Registro actualizado');
			setUserAssistance(prev => prev.map(log => 
				log.log_id === selectedLog.log_id 
					? { ...log, date_start: selectedLog.date_start, date_end: selectedLog.date_end }
					: log
			));
			setShownModal(Modals.INFO);
		}).catch(()=> setModalPrompts(['Hubo un error actualizando el registro']))
		.finally(()=> setLoading(false));
	};

	var chartOptions = useMemo(()=>({ ...CHART_OPTIONS, onClick: onBarClick }), []);

	if(loadError) { return <Header text='Error' subtext={loadError} />};

	return <div className="ar screen">
		<Groupper style={{ marginTop: 20, paddingBottom: 0 }} width={900} 
			title={
				<div style={{ display: 'flex', justifyContent: 'space-between' }}>
					<div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
						Asistencia
					</div>
					<div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
						<Button icon iconName='arrow-left' onClick={bindClick(setCurrentWeekStart, currentWeekStart.clone().subtract(7, 'days'))} size="small" color="black" disabled={!selectedUser}/>
						<div style={{ minWidth: '200px', textAlign: 'center', fontSize: '14px', fontWeight: '500' }}>
							{formatWeekRange()}
						</div>
						<Button icon iconName='arrow-right' onClick={bindClick(setCurrentWeekStart, currentWeekStart.clone().add(7, 'days'))} size="small" color="black" disabled={!selectedUser}/>
						<Button text="Regresar" onClick={bindClick(setCurrentWeekStart, moment().startOf('isoWeek'))} color="black" size="small" disabled={!selectedUser}/>
						<Button text="Descargar" iconName='download' onClick={downloadReport} color="black" size="small" loading={downloadingReport} disabled={!selectedUser || downloadingReport || !userAssistance || userAssistance.length === 0}/>
						<Button text="Buscar" iconName='user' onClick={bindClick(setShownModal, Modals.USERS)} color="black" size="small"/>
					</div>
				</div>
			}
		>
			{selectedUser && <>
				<Header text={selectedUser.name} subtext={`Usuario ID: ${selectedUser.user_id}`} style={{ marginBottom: 20}} size="large"/>
				<Statistic.Group widths={2} size="small">
					<Statistic>
						<Statistic.Value>{totalHours.toFixed(1)}h / {totalPlanned.toFixed(1)}h</Statistic.Value>
						<Statistic.Label>Trabajadas / Planificadas</Statistic.Label>
					</Statistic>
					<Statistic>
						<Statistic.Value>{userAssistance?.length || 0}</Statistic.Value>
						<Statistic.Label>Registros</Statistic.Label>
					</Statistic>
				</Statistic.Group>
				<Groupper.Divider/>
			</>}
			{loadingUser ? (
				<Header text='Cargando usuario...' iconName='spinner' loading style={{ margin: 'auto', marginTop: 100, marginBottom: 50 }} centered />
			) : !selectedUser ? (
				<Header text='Selecciona un trabajador' iconName="user" style={{ margin: 'auto', marginTop: 100, marginBottom: 50 }} centered />
			) : userAssistance === null ? (
				<Header text='Cargando asistencia...' iconName='spinner' loading style={{ margin: 'auto', marginTop: 100, marginBottom: 50 }} centered />
			) : userAssistance.length === 0 ? (
				<Header text="No se encontraron registros." iconName='face-sad-tear' style={{ margin: 'auto', marginTop: 100, marginBottom: 50 }} centered />
			) : chartData ? (
				<>
					<div style={{ height: 600}}>
						<Chart
							type='bar'
							data={chartData}
							options={chartOptions}
							plugins={CHART_PLUGINS}
						/>
					</div>
					<Groupper.Divider/>
					<Table
						striped
						fitted
						headers={['Nombre', 'Empleo', 'Dia', 'Horario', 'Entrada', 'Salida', 'Estado']}
						centeredIndexes={[ 4, 5, 6]}
						collapsingIndexes={[6]}
					>
						{userAssistance && userAssistance.length > 0 ? (
							userAssistance.map((log, idx)=>{
								var status = getCheckInStatus(log);
								var statusColor = getStatusColor(status);
								var statusLabel = getStatusLabel(status);
								return (
									<Table.Row
										key={`${log.schedule_id}-${idx}`}
										centeredIndexes={[4, 5, 6]}
										data={[
											log.name,
											log.employment_name,
											DAYS[moment.unix(log.date_start).day()],
											`${log.time_start !== null && log.time_end !== null ?
												`${String(Math.floor(log.time_start / 60)).padStart(2, '0')}:${String(log.time_start % 60).padStart(2, '0')} - ${String(Math.floor(log.time_end / 60)).padStart(2, '0')}:${String(log.time_end % 60).padStart(2, '0')}`
												: 'N/A'
											}`,
											(log.log_type_id || log.type_id === EmploymentScheduleType.REST) ? '-' : (log.date_start ? moment.unix(log.date_start).format('HH:mm') : 'N/A'),
											(log.log_type_id || log.type_id === EmploymentScheduleType.REST) ? '-' : (log.date_end && log.date_end > 0 ? moment.unix(log.date_end).format('HH:mm') : '-'),
											<Label color={statusColor}>{statusLabel}</Label>
										]}
									/>
								);
							})
						) : (
							<Table.Cell row className='empty' colSpan={7}>
								No se encontraron registros
							</Table.Cell>
						)}
					</Table>
				</>
			) : null}
			{loadError && <Message type="error" text={loadError} />}
		</Groupper>
		<SearchUserModal
			open={shownModal === Modals.USERS}
			onClose={bindClick(setShownModal, null)}
			onSelected={onUserSelect}
		/>
		<Modal open={shownModal === Modals.INFO} onClose={bindClose(setShownModal)} size="mini">
			{selectedLog && <>
				<Modal.Header>Información de asistencia</Modal.Header>
				<Modal.Content style={{ padding: 0 }}>
					<Toolbar fitted style={{ borderRadius: 0}}>
						{hasAccess(UserAccess.EMPLOYMENTS.EDIT_EMPLOYMENTS) && !selectedLog.log_type_id && selectedLog.type_id !== EmploymentScheduleType.REST && <Toolbar.Item text="Editar" iconName='edit' onClick={bindClick(setShownModal, Modals.EDIT)}/>}
					</Toolbar>
					<Table details data={[
						['Empleado', selectedLog.name],
						['Empleo', selectedLog.employment_name],
						...(selectedLog.salary ? [['Salario', '$' + addCommas(selectedLog.salary)]] : []),
						['Día', DAYS[moment.unix(selectedLog.date_start || moment().unix()).day()]],
						['Horario', `${selectedLog.time_start !== null && selectedLog.time_end !== null ?
							`${String(Math.floor(selectedLog.time_start / 60)).padStart(2, '0')}:${String(selectedLog.time_start % 60).padStart(2, '0')} - ${String(Math.floor(selectedLog.time_end / 60)).padStart(2, '0')}:${String(selectedLog.time_end % 60).padStart(2, '0')}`
							: 'N/A'
						}`],
						...(selectedLog.type_id === EmploymentScheduleType.REST && !selectedLog.log_type_id ? [['Tipo de horario', 'Descanso']] : []),
						...(selectedLog.log_type_id ? [['Tipo de registro', selectedLog.log_type_name || 'Incidencia']] : []),
						...(selectedLog.log_type_id ? [['Notas', selectedLog.notes || '-']] : []),
						...(!selectedLog.log_type_id && selectedLog.type_id !== EmploymentScheduleType.REST && selectedLog.date_start ? [['Check-In', moment.unix(selectedLog.date_start).format('DD/MM/YYYY HH:mm')]] : []),
						...(!selectedLog.log_type_id && selectedLog.type_id !== EmploymentScheduleType.REST && selectedLog.date_end && selectedLog.date_end > 0 ? [['Check-Out', moment.unix(selectedLog.date_end).format('DD/MM/YYYY HH:mm')]] : [])
					]}/>
					<Message type="error" list={modalPrompts} />
				</Modal.Content>
				<Modal.Actions>
					<Button text="Cerrar" basic onClick={bindClose(setShownModal, null)} />
				</Modal.Actions>
			</>}
		</Modal>
		<Modal open={shownModal === Modals.EDIT} onClose={bindClose(setShownModal)} size="mini">
			<Modal.Header>Editar entrada y salida</Modal.Header>
			{selectedLog && (
				<Modal.Content>
					<Input 
						label="Hora de entrada" 
						value={selectedLog.date_start} 
						onChange={(val)=>setSelectedLog({...selectedLog, date_start: val})} 
						calendar={{
							date: selectedLog.date_start,
							mode: 'datetime',
							format: 'HH:mm'
						}}
					/>
					<Input 
						label="Hora de salida" 
						value={selectedLog.date_end} 
						onChange={(val)=>setSelectedLog({...selectedLog, date_end: val})} 
						calendar={{
							date: selectedLog.date_end,
							mode: 'datetime',
							format: 'HH:mm'
						}}
					/>
					<Message type="error" list={modalPrompts} />
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button text="Cancelar" basic onClick={bindClose(setShownModal, Modals.INFO)} />
				<Button text="Guardar" color="black" onClick={edit} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default EmployeeAssistance;
